import { appData } from '../../services/workspaceStore';
import AppIcon from '../AppIcon';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { PlusCircle, User, CaretLeft, CaretRight, CalendarBlank } from '@phosphor-icons/react';
import { toast } from '../../services/notifications';
import {
  syncUserTasks,
  deleteUserTask,
  syncUserTaskHistory
} from '../../services/supabaseService';

import TodoItem from '../TodoItem';
import TaskDetailsModal from '../TaskDetailsModal';
import AddTaskModal from '../AddTaskModal';
import TaskCompletionModal from '../TaskCompletionModal';
import TaskObservationModal from '../TaskObservationModal';
import DeleteTaskModal from '../DeleteTaskModal';
import CategoryFilterBar from '../CategoryFilterBar';
import { getStoredProfiles, getTaskProfile, migrateTaskProfile } from '../../constants/profiles';
import {
  getGoogleAccessToken,
  fetchGoogleEvents,
  updateGoogleEvent,
  isBirthdayEvent
} from '../../services/googleCalendarService';

import './styles.css';
import { rollOverPendingTasks } from '../../utils/tasks';

const getTodayString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getFormattedDateLabel = (dateStr) => {
  if (!dateStr) return '';
  const today = getTodayString();
  if (dateStr === today) return 'Hoje';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const [y, m, d] = parts;
    const currentYear = String(new Date().getFullYear());
    if (y === currentYear) {
      return `${d}/${m}`;
    }
    return `${d}/${m}/${y}`;
  }
  return dateStr;
};

export const getWeekdayLabel = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const weekday = date.toLocaleDateString('pt-BR', { weekday: 'long' });
  if (!weekday) return '';
  return weekday.charAt(0).toUpperCase() + weekday.slice(1);
};

export const DEFAULT_WORK_TASKS = [];

export const ensureWorkAndRoutineTasks = (taskList) => {
  if (!Array.isArray(taskList) || taskList.length === 0) {
    return DEFAULT_WORK_TASKS;
  }

  const seenIds = new Set();
  const cleanedList = [];

  for (const t of taskList) {
    if (!t) continue;
    const taskId = String(t.id);
    if (seenIds.has(taskId)) continue;
    seenIds.add(taskId);

    // Se for uma das tarefas padrão pré-definidas (por ID) e faltar subtasks, complementa
    const def = DEFAULT_WORK_TASKS.find(w => String(w.id) === taskId);
    if (def && (!Array.isArray(t.subtasks) || t.subtasks.length === 0)) {
      cleanedList.push({
        ...t,
        subtasks: def.subtasks || []
      });
    } else {
      cleanedList.push(t);
    }
  }

  // IMPORTANTE: NÃO insere missingWork automaticamente!
  // Preserva estritamente a ordem definida pelo usuário (não reordena por horário).
  return cleanedList;
};

export const initialTaskTemplates = [];

export const POMODORO_CONFIG = { Focus: 25, ShortBreak: 5, LongBreak: 15, cycles: 4 };

function DailyPlanner({
  onPomodoroComplete,
  isDarkMode,
  toggleDarkMode,
  templates: propTemplates,
  setTemplates: propSetTemplates,
  user,
  onOpenAuthModal,
  calendarTaskToAdd,
  onClearCalendarTaskToAdd,
  selectedDate: propSelectedDate,
  setSelectedDate: propSetSelectedDate
}) {
  const [internalSelectedDate, setInternalSelectedDate] = useState(() => {
    const saved = appData.getItem('selected_planner_date');
    if (saved && /^\d{4}-\d{2}-\d{2}$/.test(saved)) {
      return saved;
    }
    return getTodayString();
  });

  const selectedDate = propSelectedDate || internalSelectedDate;
  const setSelectedDate = propSetSelectedDate || setInternalSelectedDate;

  useEffect(() => {
    appData.setItem('selected_planner_date', selectedDate);
  }, [selectedDate]);

  const [tasks, setTasks] = useState(() => JSON.parse(appData.getItem('daily_tasks') || '[]').map(migrateTaskProfile));

  const [isTasksLoaded, setIsTasksLoaded] = useState(false);
  const [historyLoadedFor, setHistoryLoadedFor] = useState(undefined);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskForCompletion, setTaskForCompletion] = useState(null);
  const [taskForObservation, setTaskForObservation] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(() => {
    const saved = appData.getItem('selected_planner_profile');
    return saved || 'Trabalho';
  });
  useEffect(() => {
    appData.setItem('selected_planner_profile', selectedProfile);
  }, [selectedProfile]);
  const [activeTimer, setActiveTimer] = useState({ taskId: null, totalSeconds: 0, phase: 'Focus', isRunning: false, pomodoroCycle: 0, type: null, config: null });
  const [currentTimeDisplay, setCurrentTimeDisplay] = useState('00:00');
  const audioContextRef = useRef(null);
  const loadedUserIdRef = useRef(null);

  const [internalTemplates, setInternalTemplates] = useState(() => {
    const savedTemplates = appData.getItem('custom_task_templates');
    if (savedTemplates) {
      try {
        return JSON.parse(savedTemplates).map(migrateTaskProfile);
      } catch (e) {
        return initialTaskTemplates;
      }
    }
    return initialTaskTemplates;
  });

  const templates = propTemplates || internalTemplates;
  const setTemplates = propSetTemplates || setInternalTemplates;

  const [plannerProfiles, setPlannerProfiles] = useState(() => getStoredProfiles());

  const [calendarEvents, setCalendarEvents] = useState(() => {
    try {
      const saved = appData.getItem('google_calendar_events');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return [];
  });

  const [calendarCompletedMap, setCalendarCompletedMap] = useState(() => {
    try {
      const saved = appData.getItem('calendar_completed_map');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {};
  });

  const [calendarStatusMap, setCalendarStatusMap] = useState(() => {
    try {
      const saved = appData.getItem('calendar_status_map');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {};
  });

  const [taskHistory, setTaskHistory] = useState(() => {
    try {
      const saved = appData.getItem('daily_task_history');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {};
  });

  const [hiddenCalendarEventIds, setHiddenCalendarEventIds] = useState(() => {
    try {
      const saved = appData.getItem('calendar_hidden_ids');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {};
  });

  const [calendarPriorityMap, setCalendarPriorityMap] = useState(() => {
    try {
      const saved = appData.getItem('calendar_priority_map');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {};
  });

  useEffect(() => {
    const handleEventsUpdated = (e) => {
      if (e?.detail && Array.isArray(e.detail)) {
        setCalendarEvents(e.detail);
      } else {
        try {
          const saved = appData.getItem('google_calendar_events');
          if (saved) setCalendarEvents(JSON.parse(saved));
        } catch (err) {}
      }
    };

    window.addEventListener('google-calendar-events-updated', handleEventsUpdated);

    async function syncCalendarSilently() {
      try {
        const token = await getGoogleAccessToken();
        if (token) {
          const res = await fetchGoogleEvents(token);
          if (!res.error && !res.isUnauthorized && Array.isArray(res.events)) {
            const merged = [...new Map([...JSON.parse(appData.getItem('google_calendar_events') || '[]'), ...res.events].map(event => [event.id, event])).values()];
            setCalendarEvents(merged);
            appData.setItem('google_calendar_events', JSON.stringify(merged));
          }
        }
      } catch (err) {
        console.warn('Erro ao sincronizar eventos em background:', err);
      }
    }
    syncCalendarSilently();

    return () => {
      window.removeEventListener('google-calendar-events-updated', handleEventsUpdated);
    };
  }, []);

  useEffect(() => {
    const handleCatsChanged = () => setPlannerProfiles(getStoredProfiles());
    window.addEventListener('coach-profiles-changed', handleCatsChanged);
    return () => window.removeEventListener('coach-profiles-changed', handleCatsChanged);
  }, []);

  // Efeito para adicionar tarefas vindas da Agenda
  useEffect(() => {
    if (calendarTaskToAdd) {
      const taskDate = calendarTaskToAdd.date || selectedDate;
      const newTask = {
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        text: calendarTaskToAdd.text,
        profile: calendarTaskToAdd.profile || 'Pessoal',
        description: calendarTaskToAdd.description || '',
        time: calendarTaskToAdd.time || '09:00',
        period: calendarTaskToAdd.period || 'Manhã',
        completed: false,
        subtasks: calendarTaskToAdd.subtasks || [],
        color: calendarTaskToAdd.color || null,
        date: taskDate
      };
      setTasks(prev => [...prev, newTask]);
      if (onClearCalendarTaskToAdd) onClearCalendarTaskToAdd();
    }
  }, [calendarTaskToAdd, selectedDate, onClearCalendarTaskToAdd]);

  useEffect(() => {
    loadedUserIdRef.current = user.id;
    setIsTasksLoaded(true);
    setHistoryLoadedFor(user.id);
  }, [user.id]);

  useEffect(() => {
    if (isTasksLoaded && loadedUserIdRef.current === user.id) syncUserTasks(user.id, tasks).catch(() => {});
  }, [tasks, user.id, isTasksLoaded]);

  useEffect(() => {
    if (isTasksLoaded) syncUserTaskHistory(user.id, taskHistory).catch(() => {});
  }, [taskHistory, user.id, isTasksLoaded]);

  useEffect(() => {
    if (!isTasksLoaded || historyLoadedFor !== user.id) return;
    const rollOver = () => setTasks(prev => rollOverPendingTasks(prev, taskHistory));
    rollOver();
    const interval = setInterval(rollOver, 30000);
    window.addEventListener('focus', rollOver);
    return () => { clearInterval(interval); window.removeEventListener('focus', rollOver); };
  }, [isTasksLoaded, historyLoadedFor, user.id, taskHistory]);

  // Migra status locais antigos de compromissos da Google Agenda para o taskHistory para sincronização na nuvem
  useEffect(() => {
    try {
      const savedStatus = appData.getItem('calendar_status_map');
      const savedCompleted = appData.getItem('calendar_completed_map');
      let statusObj = savedStatus ? JSON.parse(savedStatus) : {};
      let completedObj = savedCompleted ? JSON.parse(savedCompleted) : {};

      const migratedEntries = {};
      let hasMigration = false;

      Object.entries(statusObj).forEach(([k, val]) => {
        const parts = k.split('_');
        if (parts.length >= 2) {
          const date = parts[0];
          const eventId = parts.slice(1).join('_');
          const historyKey = `calendar-${eventId}_${date}`;
          if (!taskHistory[historyKey]) {
            migratedEntries[historyKey] = {
              id: historyKey,
              taskId: `calendar-${eventId}`,
              date: date,
              status: (typeof val === 'object' && val?.status) ? val.status : 'completed',
              observation: (typeof val === 'object' && val?.observation) ? val.observation : '',
              completedAt: new Date().toISOString(),
              startedAt: null,
              subtasks: []
            };
            hasMigration = true;
          }
        }
      });

      Object.entries(completedObj).forEach(([k, isDone]) => {
        if (!isDone) return;
        const parts = k.split('_');
        if (parts.length >= 2) {
          const date = parts[0];
          const eventId = parts.slice(1).join('_');
          const historyKey = `calendar-${eventId}_${date}`;
          if (!taskHistory[historyKey] && !migratedEntries[historyKey]) {
            migratedEntries[historyKey] = {
              id: historyKey,
              taskId: `calendar-${eventId}`,
              date: date,
              status: 'completed',
              observation: '',
              completedAt: new Date().toISOString(),
              startedAt: null,
              subtasks: []
            };
            hasMigration = true;
          }
        }
      });

      if (hasMigration) {
        setTaskHistory(prev => ({ ...prev, ...migratedEntries }));
      }
    } catch (e) {
      console.warn('Erro ao migrar status de compromissos para taskHistory:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const renameProfile = ({ detail: { previous, next } }) => {
      setTasks(items => items.map(task => getTaskProfile(task) === previous ? { ...task, profile: next } : task));
      setSelectedProfile(current => current === previous ? (next || '__none__') : current);
      setCalendarPriorityMap(items => {
        const updated = { ...items };
        calendarEvents.forEach(event => {
          const settings = items[event.id] || {};
          if ((settings.profile === undefined ? 'Pessoal' : settings.profile) === previous) updated[event.id] = { ...settings, profile: next };
        });
        appData.setItem('calendar_priority_map', JSON.stringify(updated));
        return updated;
      });
    };
    window.addEventListener('coach-profile-renamed', renameProfile);
    return () => window.removeEventListener('coach-profile-renamed', renameProfile);
  }, [calendarEvents]);

  const handleMoveToToday = (taskId) => {
    setTasks(prev => prev.map(t => {
      if (t.id === taskId) {
        return {
          ...t,
          date: todayStr
        };
      }
      return t;
    }));
    toast.success('Tarefa transferida para a data de hoje!');
  };


  const handlePrevDay = () => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d);
    dateObj.setDate(dateObj.getDate() - 1);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    setSelectedDate(`${year}-${month}-${day}`);
  };

  const handleNextDay = () => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d);
    dateObj.setDate(dateObj.getDate() + 1);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    setSelectedDate(`${year}-${month}-${day}`);
  };

  const handleAddTask = (newTask, saveAsTemplate) => {
    const uniqueId = `${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
    const taskWithDate = {
      ...newTask,
      id: uniqueId,
      date: newTask.date || selectedDate,
      isRecurring: Boolean(newTask.isRecurring),
      recurringDays: newTask.isRecurring ? (newTask.recurringDays || []).map(Number).filter(n => !isNaN(n)) : []
    };
    setTasks(prevTasks => [...prevTasks, taskWithDate]);
    if (saveAsTemplate) {
      const newTemplate = {
        id: Date.now().toString(),
        text: newTask.text,
        category: newTask.category || '',
        profile: newTask.profile || null,
        color: newTask.color || '',
        description: newTask.description || '',
        subtasks: newTask.subtasks || [],
        isRecurring: Boolean(newTask.isRecurring),
        recurringDays: newTask.isRecurring ? (newTask.recurringDays || []).map(Number).filter(n => !isNaN(n)) : []
      };
      setTemplates(prev => [...prev, newTemplate]);
    }
  };

  const handleUpdateTask = async (updatedTask) => {
    if (String(updatedTask.id).startsWith('calendar-')) {
      const origId = updatedTask.calendarOriginalId;
      const token = await getGoogleAccessToken();
      await updateGoogleEvent(token, origId, updatedTask);
      if (origId) {
        setCalendarPriorityMap(prev => {
          const next = {
            ...prev,
            [origId]: {
              color: updatedTask.color || '#10b981',
              category: updatedTask.category || null,
              profile: updatedTask.profile || null,
              difficulty: updatedTask.difficulty || 'low',
              period: updatedTask.period,
              description: updatedTask.description
            }
          };
          try {
            appData.setItem('calendar_priority_map', JSON.stringify(next));
          } catch (e) {}
          return next;
        });
      }
      setCalendarEvents(prev => {
        const next = prev.map(evt => {
          if (evt.id === updatedTask.calendarOriginalId) {
            return {
              ...evt,
              title: updatedTask.text,
              date: updatedTask.date,
              description: updatedTask.description,
              period: updatedTask.period,
              time: updatedTask.time || evt.time,
            };
          }
          return evt;
        });
        try {
          appData.setItem('google_calendar_events', JSON.stringify(next));
        } catch (e) {}
        return next;
      });
      setSelectedTask(null);
      return;
    }

    const sanitizedTask = {
      ...updatedTask,
      isRecurring: Boolean(updatedTask.isRecurring),
      recurringDays: updatedTask.isRecurring ? (updatedTask.recurringDays || []).map(Number).filter(n => !isNaN(n)) : []
    };

    // 1. Salva o estado das subtarefas daquele dia específico no taskHistory
    const historyKey = `${sanitizedTask.id}_${selectedDate}`;
    setTaskHistory(prev => {
      const existing = prev[historyKey] || {};
      return {
        ...prev,
        [historyKey]: {
          ...existing,
          id: historyKey,
          taskId: String(sanitizedTask.id),
          date: selectedDate,
          status: existing.status || 'pending',
          observation: existing.observation || '',
          subtasks: sanitizedTask.subtasks || []
        }
      };
    });

    // 2. Cria o modelo base de subtarefas (com completed: false) para que outros dias não sejam afetados
    const baseSubtasksModel = (sanitizedTask.subtasks || []).map(st => ({
      id: st.id,
      text: st.text,
      completed: false
    }));

    setTasks(prevTasks => {
      const updatedList = prevTasks.map(t => {
        if (t.id !== sanitizedTask.id) return t;
        return {
          ...t,
          text: sanitizedTask.text,
          period: sanitizedTask.period,
          category: sanitizedTask.category,
          profile: sanitizedTask.profile,
          color: sanitizedTask.color,
          date: sanitizedTask.date,
          isRecurring: sanitizedTask.isRecurring,
          recurringDays: sanitizedTask.recurringDays,
          description: sanitizedTask.description,
          subtasks: baseSubtasksModel
        };
      });
      return updatedList;
    });
    setSelectedTask(null);
  };

  const handleDropFromCalendar = (e) => {
    e.preventDefault();
    try {
      const dataStr = e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
      if (dataStr) {
        const item = JSON.parse(dataStr);
        if (item.text) {
          const newTask = {
            id: `${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
            text: item.text,
            profile: item.profile || 'Pessoal',
            description: item.description || '',
            time: item.time || '09:00',
            period: item.period || 'Manhã',
            completed: false,
            subtasks: item.subtasks || [],
            color: item.color || null,
            date: selectedDate
          };
          setTasks(prev => [...prev, newTask]);
        }
      }
    } catch (err) {
      console.error('Erro ao processar drop da agenda:', err);
    }
  };

  const handleDragOverFromCalendar = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const speak = useCallback((text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'pt-BR';
      utterance.rate = 1;
      window.speechSynthesis.speak(utterance);
    }
  }, []);

  const playBeep = useCallback(() => {
    if (!audioContextRef.current) return;
    const ctx = audioContextRef.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
  }, []);

  const startNextPhase = useCallback(() => {
    const { taskId, phase, pomodoroCycle, config } = activeTimer;
    let nextPhase = 'Focus';
    let nextCycle = pomodoroCycle;
    let durationMinutes = config.Focus;
    let speechMessage = '';

    if (phase === 'Focus') {
      onPomodoroComplete();
      nextCycle += 1;
      if (nextCycle % config.cycles === 0) {
        nextPhase = 'LongBreak';
        durationMinutes = config.LongBreak;
        speechMessage = `Excelente trabalho! Você concluiu ${config.cycles} ciclos de foco. É hora do seu descanso longo de ${config.LongBreak} minutos. Pode relaxar!`;
      } else {
        nextPhase = 'ShortBreak';
        durationMinutes = config.ShortBreak;
        speechMessage = `Parabéns! Ciclo de foco concluído. É hora da sua pausa curta de ${config.ShortBreak} minutos. Respire e relaxe um pouco!`;
      }
    } else {
      nextPhase = 'Focus';
      durationMinutes = config.Focus;
      const taskText = tasks.find(t => t.id === taskId)?.text || 'sua tarefa';
      speechMessage = `Pausa concluída! É hora de voltar ao foco na tarefa: ${taskText}. Bom trabalho!`;
    }

    speak(speechMessage);
    playBeep();

    setActiveTimer(prev => ({
      ...prev,
      phase: nextPhase,
      totalSeconds: durationMinutes * 60,
      pomodoroCycle: nextCycle,
      isRunning: true
    }));
  }, [activeTimer, playBeep, speak, tasks, onPomodoroComplete]);

  const handleCancelTimer = useCallback(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setActiveTimer({ taskId: null, totalSeconds: 0, phase: 'Focus', isRunning: false, pomodoroCycle: 0, type: null, config: null });
  }, []);

  useEffect(() => {
    let interval = null;
    if (activeTimer.isRunning && activeTimer.totalSeconds > 0) {
      interval = setInterval(() => {
        setActiveTimer(prev => ({ ...prev, totalSeconds: prev.totalSeconds - 1 }));
      }, 1000);
    } else if (activeTimer.isRunning && activeTimer.totalSeconds === 0) {
      if (activeTimer.type === 'pomodoro') {
        startNextPhase();
      } else {
        const completedTask = tasks.find(t => t.id === activeTimer.taskId);
        const endMessage = `Tempo para ${completedTask?.text || 'a tarefa'} concluído!`;
        speak(endMessage);
        toast.success(endMessage, { icon: <AppIcon name="trophy" /> });
        handleCancelTimer();
      }
    }
    return () => clearInterval(interval);
  }, [activeTimer, startNextPhase, tasks, speak, handleCancelTimer]);

  const handleStartTimer = (taskId, config, type) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const currentTask = tasks.find(t => t.id === taskId) || calendarTasksForSelectedDate.find(t => t.id === taskId);
    speak(`Iniciando ${config.ShortBreak ? 'ciclo' : 'timer'} de ${config.Focus} minutos para a tarefa ${currentTask?.text || 'selecionada'}.`);

    const nowIso = new Date().toISOString();
    const historyKey = `${taskId}_${selectedDate}`;

    // 1. Grava o horário de início única e exclusivamente no histórico da data selecionada
    setTaskHistory(prev => {
      const existing = prev[historyKey] || {};
      if (existing.startedAt) return prev;
      return {
        ...prev,
        [historyKey]: {
          ...existing,
          id: historyKey,
          taskId: String(taskId),
          date: selectedDate,
          status: existing.status || 'pending',
          observation: existing.observation || '',
          completedAt: existing.completedAt || null,
          startedAt: nowIso,
          subtasks: existing.subtasks || (currentTask?.subtasks ? currentTask.subtasks.map(st => ({ ...st, completed: false })) : [])
        }
      };
    });

    // 2. Se for tarefa comum e NÃO for recorrente, atualiza em tasks apenas para a data correspondente
    setTasks(prev => prev.map(t => {
      if (t.id === taskId && !t.isRecurring && (t.date === selectedDate || (!t.date && selectedDate === todayStr))) {
        return {
          ...t,
          startedAt: t.startedAt || nowIso
        };
      }
      return t;
    }));

    setActiveTimer({ taskId, totalSeconds: config.Focus * 60, phase: 'Focus', isRunning: true, pomodoroCycle: 0, type, config });
  };

  const handlePauseResumeTimer = () => {
    if (activeTimer.totalSeconds > 0) {
      setActiveTimer(prev => ({ ...prev, isRunning: !prev.isRunning }));
    }
  };

  const getDayOfWeek = (dateStr) => {
    if (!dateStr) return new Date().getDay();
    const parts = String(dateStr).split('-');
    if (parts.length === 3) {
      const [y, m, d] = parts.map(Number);
      // Usar 12:00:00 (meio-dia) para evitar desvios por fuso horário local/UTC
      return new Date(y, m - 1, d, 12, 0, 0).getDay();
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getDay()) ? new Date().getDay() : parsed.getDay();
  };

  const isTaskForSelectedDate = (t, dateStr) => {
    if (!t || !dateStr) return false;

    // Se estiver marcado como deleted no taskHistory para esta data
    const historyKey = `${t.id}_${dateStr}`;
    if (taskHistory && taskHistory[historyKey]?.status === 'deleted') {
      return false;
    }

    const dayOfWeek = getDayOfWeek(dateStr);

    if (Boolean(t.isRecurring)) {
      const days = (t.recurringDays || []).map(Number).filter(n => !isNaN(n));
      // Se não há dias definidos na tarefa recorrente, não exibe
      if (days.length === 0) return false;
      // Só deve aparecer se o dia da semana atual estiver nos dias selecionados
      if (!days.includes(dayOfWeek)) return false;
      // Se tiver data de início/criação, não exibe em dias anteriores
      if (t.date && /^\d{4}-\d{2}-\d{2}$/.test(t.date) && dateStr < t.date) {
        return false;
      }
      // Se tiver data de término (recurringUntil), não exibe a partir desta data nem após ela
      if (t.recurringUntil && /^\d{4}-\d{2}-\d{2}$/.test(t.recurringUntil) && dateStr >= t.recurringUntil) {
        return false;
      }
      // Se esta data específica foi excluída da recorrência
      if (Array.isArray(t.deletedDates) && t.deletedDates.includes(dateStr)) {
        return false;
      }
      return true;
    }

    // Tarefa pontual: só deve aparecer na sua data exata
    const taskDate = t.date || todayStr;
    return taskDate === dateStr;
  };

  const handleToggle = (id) => {
    const task = processedTasks.find(t => t.id === id);
    if (!task) return;
    if (task.isBirthday) return;

    if (activeTimer.taskId === id) {
      const userConfirmed = window.confirm("A atividade está em andamento. Deseja realmente parar o timer e definir a conclusão?");
      if (userConfirmed) {
        handleCancelTimer();
      } else {
        return;
      }
    }

    setTaskForCompletion(task);
  };

  const handleSaveCompletion = (taskId, { status, observation }) => {
    if (String(taskId).startsWith('calendar-')) {
      const calTask = calendarTasksForSelectedDate.find(t => t.id === taskId);
      if (!calTask) return;
      const key = `${selectedDate}_${calTask.calendarOriginalId}`;
      const isCompleted = status === 'completed';
      const historyKey = `${taskId}_${selectedDate}`;
      const nowIso = new Date().toISOString();

      setCalendarStatusMap(prev => {
        const next = { ...prev, [key]: { status, observation } };
        try {
          appData.setItem('calendar_status_map', JSON.stringify(next));
        } catch (e) {}
        return next;
      });

      setCalendarCompletedMap(prev => {
        const next = { ...prev, [key]: isCompleted };
        try {
          appData.setItem('calendar_completed_map', JSON.stringify(next));
        } catch (e) {}
        return next;
      });

      setTaskHistory(prev => ({
        ...prev,
        [historyKey]: {
          id: historyKey,
          taskId: String(taskId),
          date: selectedDate,
          status,
          observation: observation || '',
          completedAt: (status === 'completed' || status === 'failed') ? nowIso : null,
          startedAt: null,
          subtasks: []
        }
      }));

      setTaskForCompletion(null);
      toast.success(status === 'completed' ? 'Compromisso concluído!' : 'Compromisso marcado como não concluído.');
      return;
    }

    if (activeTimer.taskId === taskId) {
      handleCancelTimer();
    }

    const currentTask = processedTasks.find(t => t.id === taskId);
    const historyKey = `${taskId}_${selectedDate}`;
    const nowIso = new Date().toISOString();

    // 1. Grava no taskHistory a ocorrência exclusiva deste dia
    setTaskHistory(prev => {
      const existing = prev[historyKey] || {};
      return {
        ...prev,
        [historyKey]: {
          ...existing,
          id: historyKey,
          taskId: String(taskId),
          date: selectedDate,
          status,
          observation: observation || '',
          completedAt: (status === 'completed' || status === 'failed') ? nowIso : null,
          startedAt: currentTask?.startedAt || existing.startedAt || null,
          subtasks: currentTask?.subtasks || existing.subtasks || []
        }
      };
    });

    // 2. Mantém compatibilidade com a tabela base tasks
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;

      if (t.isRecurring) {
        const currentDates = Array.isArray(t.completedDates) ? t.completedDates : [];
        const currentFailedDates = Array.isArray(t.failedDates) ? t.failedDates : [];
        const currentObs = { ...(t.dateObservations || {}) };

        let updatedDates = currentDates.filter(d => d !== selectedDate);
        let updatedFailedDates = currentFailedDates.filter(d => d !== selectedDate);

        if (status === 'completed') {
          updatedDates.push(selectedDate);
        } else if (status === 'failed') {
          updatedFailedDates.push(selectedDate);
        }

        if (observation) {
          currentObs[selectedDate] = observation;
        } else {
          delete currentObs[selectedDate];
        }

        const isTodayDone = selectedDate === todayStr ? (status === 'completed') : (updatedDates.includes(todayStr));
        const { startedAt, completedAt, ...cleanRecurring } = t;
        return {
          ...cleanRecurring,
          completedDates: updatedDates,
          failedDates: updatedFailedDates,
          dateObservations: currentObs,
          completed: isTodayDone,
          status: selectedDate === todayStr ? status : t.status
        };
      }

      if (t.date === selectedDate || (!t.date && selectedDate === todayStr)) {
        return {
          ...t,
          status,
          completed: status === 'completed',
          observation: observation || '',
          completedAt: (status === 'completed' || status === 'failed') ? nowIso : null
        };
      }

      return t;
    }));

    setTaskForCompletion(null);
    toast.success(status === 'completed' ? 'Tarefa concluída!' : 'Tarefa marcada como não concluída.');
  };

  const handleResetPending = (taskId) => {
    if (String(taskId).startsWith('calendar-')) {
      const calTask = calendarTasksForSelectedDate.find(t => t.id === taskId);
      if (!calTask) return;
      const key = `${selectedDate}_${calTask.calendarOriginalId}`;
      const historyKey = `${taskId}_${selectedDate}`;

      setCalendarStatusMap(prev => {
        const next = { ...prev };
        delete next[key];
        try {
          appData.setItem('calendar_status_map', JSON.stringify(next));
        } catch (e) {}
        return next;
      });

      setCalendarCompletedMap(prev => {
        const next = { ...prev };
        delete next[key];
        try {
          appData.setItem('calendar_completed_map', JSON.stringify(next));
        } catch (e) {}
        return next;
      });

      setTaskHistory(prev => {
        const next = { ...prev };
        if (next[historyKey]) {
          next[historyKey] = {
            ...next[historyKey],
            status: 'pending',
            observation: '',
            completedAt: null
          };
        }
        return next;
      });

      setTaskForCompletion(null);
      toast.info('Compromisso voltou para o estado pendente.');
      return;
    }

    const historyKey = `${taskId}_${selectedDate}`;
    setTaskHistory(prev => {
      const next = { ...prev };
      if (next[historyKey]) {
        next[historyKey] = {
          ...next[historyKey],
          status: 'pending',
          observation: '',
          completedAt: null
        };
      }
      return next;
    });

    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;

      if (t.isRecurring) {
        const currentDates = Array.isArray(t.completedDates) ? t.completedDates : [];
        const currentFailedDates = Array.isArray(t.failedDates) ? t.failedDates : [];
        const currentObs = { ...(t.dateObservations || {}) };

        delete currentObs[selectedDate];

        const updatedDates = currentDates.filter(d => d !== selectedDate);
        const updatedFailedDates = currentFailedDates.filter(d => d !== selectedDate);

        const isTodayDone = selectedDate === todayStr ? false : (updatedDates.includes(todayStr));

        const { startedAt, completedAt, ...cleanRecurring } = t;
        return {
          ...cleanRecurring,
          completedDates: updatedDates,
          failedDates: updatedFailedDates,
          dateObservations: currentObs,
          completed: isTodayDone,
          status: selectedDate === todayStr ? 'pending' : t.status
        };
      }

      if (t.date === selectedDate || (!t.date && selectedDate === todayStr)) {
        return {
          ...t,
          status: 'pending',
          completed: false,
          observation: '',
          completedAt: null
        };
      }

      return t;
    }));

    setTaskForCompletion(null);
    toast.info('Tarefa voltou para o estado pendente.');
  };

  const handleRemove = (id) => {
    if (String(id).startsWith('calendar-')) {
      const calTask = calendarTasksForSelectedDate.find(t => t.id === id);
      if (!calTask) return;
      const key = `${selectedDate}_${calTask.calendarOriginalId}`;
      setHiddenCalendarEventIds(prev => {
        const next = { ...prev, [key]: true };
        try {
          appData.setItem('calendar_hidden_ids', JSON.stringify(next));
        } catch (e) {}
        return next;
      });
      toast.success('Compromisso removido da lista do dia.');
      return;
    }

    const targetTask = tasks.find(t => t.id === id) || processedTasks.find(t => t.id === id);
    if (!targetTask) return;

    // Se for uma tarefa recorrente, abre o modal de opções de exclusão
    if (targetTask.isRecurring) {
      setTaskToDelete(targetTask);
      return;
    }

    // Tarefa simples (não recorrente): apaga diretamente
    if (activeTimer.taskId === id) handleCancelTimer();
    setTasks(prev => prev.filter(t => t.id !== id));
    deleteUserTask(user.id, id).catch(() => {});
    toast.success('Tarefa removida.');
  };

  const handleDeleteSingleDay = () => {
    if (!taskToDelete) return;
    const taskId = taskToDelete.id;
    if (activeTimer.taskId === taskId) handleCancelTimer();

    const historyKey = `${taskId}_${selectedDate}`;

    // 1. Marca no taskHistory como deleted para sincronização em nuvem
    setTaskHistory(prev => ({
      ...prev,
      [historyKey]: {
        ...(prev[historyKey] || {}),
        id: historyKey,
        taskId: String(taskId),
        date: selectedDate,
        status: 'deleted',
        completedAt: null
      }
    }));

    // 2. Adiciona a data no array deletedDates da tarefa
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      const currentDeleted = Array.isArray(t.deletedDates) ? t.deletedDates : [];
      if (currentDeleted.includes(selectedDate)) return t;
      return {
        ...t,
        deletedDates: [...currentDeleted, selectedDate]
      };
    }));

    setTaskToDelete(null);
    toast.success('Tarefa removida apenas deste dia.');
  };

  const handleDeleteFromDayForward = () => {
    if (!taskToDelete) return;
    const taskId = taskToDelete.id;
    if (activeTimer.taskId === taskId) handleCancelTimer();

    // Define recurringUntil como a data selecionada (interrompe repetições a partir desta data)
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      return {
        ...t,
        recurringUntil: selectedDate
      };
    }));

    setTaskToDelete(null);
    toast.success('Tarefa cancelada a partir deste dia.');
  };

  const handleDeleteAll = () => {
    if (!taskToDelete) return;
    const taskId = taskToDelete.id;
    if (activeTimer.taskId === taskId) handleCancelTimer();

    setTasks(prev => prev.filter(t => t.id !== taskId));
    deleteUserTask(user.id, taskId).catch(() => {});
    setTaskToDelete(null);
    toast.success('Todas as repetições da tarefa foram removidas.');
  };

  const handleSelectCategory = (catName) => {
    setSelectedProfile(catName);
    try {
      appData.setItem('selected_planner_profile', catName);
    } catch (e) {}
  };

  const handleOnDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    if (String(active.id).startsWith('calendar-') || String(over.id).startsWith('calendar-')) {
      return;
    }

    setTasks((allTasks) => {
      const activeTask = allTasks.find(t => t.id === active.id);
      const overTask = allTasks.find(t => t.id === over.id);

      if (!activeTask || !overTask) return allTasks;

      const activePeriod = getPeriod(activeTask);
      const overPeriod = getPeriod(overTask);

      // Restringe o drag and drop estritamente ao mesmo período!
      if (activePeriod !== overPeriod) {
        return allTasks;
      }

      // Tarefas deste dia pertencentes a este período
      const periodTasks = allTasks.filter(t =>
        isTaskForSelectedDate(t, selectedDate) && getPeriod(t) === activePeriod
      );

      const oldIndex = periodTasks.findIndex(t => t.id === active.id);
      const newIndex = periodTasks.findIndex(t => t.id === over.id);

      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
        return allTasks;
      }

      // Reordena apenas as tarefas daquele período
      const reorderedPeriodTasks = arrayMove(periodTasks, oldIndex, newIndex);

      // Se todas as tarefas do período têm horários definidos, ajusta os horários
      // para acompanhar a nova ordem, para que fiquem cronologicamente consistentes
      const existingTimes = periodTasks.map(t => t.time).filter(Boolean);
      let adjustedTasks = reorderedPeriodTasks;

      if (existingTimes.length === periodTasks.length) {
        const sortedTimes = [...existingTimes].sort((a, b) => a.localeCompare(b));
        adjustedTasks = reorderedPeriodTasks.map((t, idx) => ({
          ...t,
          time: sortedTimes[idx] || t.time
        }));
      }

      // Atualiza allTasks mantendo as tarefas de outros períodos e outros dias nos seus lugares
      const periodTaskIds = new Set(periodTasks.map(t => t.id));
      const result = [];
      let periodInserted = false;

      for (const t of allTasks) {
        if (periodTaskIds.has(t.id)) {
          if (!periodInserted) {
            result.push(...adjustedTasks);
            periodInserted = true;
          }
        } else {
          result.push(t);
        }
      }

      // Envia a nova ordem ao banco pela fila de gravação da conta.
      try {
        const orderIds = result.map(t => String(t.id));
        appData.setItem('daily_tasks_order', JSON.stringify(orderIds));
        appData.setItem('daily_tasks', JSON.stringify(result));
      } catch (err) {}

      return result;
    });
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  useEffect(() => {
    setCurrentTimeDisplay(formatTime(activeTimer.totalSeconds));
  }, [activeTimer.totalSeconds]);

  const getPeriod = (t) => {
    if (!t) return 'Manhã';
    if (t.period) return t.period;
    if (!t.time) return 'Manhã';
    const h = parseInt(t.time.split(':')[0], 10);
    if (h >= 12 && h < 18) return 'Tarde';
    if (h >= 18) return 'Noite';
    return 'Manhã';
  };

  const todayStr = getTodayString();
  const tasksForSelectedDate = tasks.filter(t => isTaskForSelectedDate(t, selectedDate));

  const uniqueCalendarMap = new Map();
  const existingTitles = new Set(
    tasksForSelectedDate.map(t => (t.text || '').toLowerCase().trim())
  );

  (calendarEvents || []).forEach(evt => {
    if (!evt || !evt.id) return;
    const key = `${selectedDate}_${evt.id}`;
    if (hiddenCalendarEventIds[key]) return;

    const isBirthday = isBirthdayEvent(evt);
    let matchesDate = false;

    if (evt.date === selectedDate) {
      matchesDate = true;
    } else if (isBirthday && evt.date && evt.date.length >= 10 && selectedDate.length >= 10) {
      matchesDate = (evt.date.slice(5) === selectedDate.slice(5));
    }

    if (!matchesDate) return;

    const normTitle = (evt.title || evt.text || '').toLowerCase().trim();
    if (!normTitle) return;

    // Se já existe uma tarefa manual com o mesmo título, evita duplicata
    if (existingTitles.has(normTitle)) return;

    // Chave de desduplicação:
    // Para aniversários: normTitle (ex: 'aniversário mãe'), garantindo apenas 1 card mesmo que haja instâncias em outros anos
    // Para compromissos normais: normTitle + horário
    const dedupeKey = isBirthday ? `bday_${normTitle}` : `evt_${normTitle}_${evt.time || 'all'}`;

    if (!uniqueCalendarMap.has(dedupeKey)) {
      uniqueCalendarMap.set(dedupeKey, evt);
    } else {
      const existing = uniqueCalendarMap.get(dedupeKey);
      if (evt.date === selectedDate && existing.date !== selectedDate) {
        uniqueCalendarMap.set(dedupeKey, evt);
      }
    }
  });

  const calendarTasksForSelectedDate = Array.from(uniqueCalendarMap.values()).map(evt => {
    const isBirthday = isBirthdayEvent(evt);
    const key = `${selectedDate}_${evt.id}`;
    const historyKey = `calendar-${evt.id}_${selectedDate}`;
    const historyEntry = taskHistory ? taskHistory[historyKey] : null;
    const calStatusObj = calendarStatusMap[key];
    const legacyCompleted = Boolean(calendarCompletedMap[key]);

    let isCompleted = false;
    let status = 'pending';
    let observation = '';
    let startedAt = null;
    let completedAt = null;

    if (!isBirthday) {
      if (historyEntry && historyEntry.status) {
        status = historyEntry.status;
        isCompleted = status === 'completed';
        observation = historyEntry.observation || '';
        startedAt = historyEntry.startedAt || null;
        completedAt = historyEntry.completedAt || null;
      } else if (calStatusObj) {
        status = calStatusObj.status || 'pending';
        isCompleted = status === 'completed';
        observation = calStatusObj.observation || '';
      } else if (legacyCompleted) {
        status = 'completed';
        isCompleted = true;
      }
    }

    const customPrio = calendarPriorityMap[evt.id] || {};
    const priorityColor = isBirthday ? '#a855f7' : (customPrio.color || '#10b981');
    const calendarBgColor = isBirthday ? '#a855f7' : (evt.calendarColor || evt.color || '#0284c7');

    return {
      id: `calendar-${evt.id}`,
      calendarOriginalId: evt.id,
      text: evt.title || evt.text || 'Compromisso',
      description: customPrio.description || evt.description || '',
      time: evt.time || '09:00',
      period: customPrio.period || evt.period || 'Manhã',
      category: customPrio.category || null,
      profile: customPrio.profile === undefined ? 'Pessoal' : customPrio.profile,
      calendarColor: calendarBgColor,
      color: priorityColor,
      difficulty: customPrio.difficulty || 'low',
      completed: isCompleted,
      status,
      observation,
      startedAt,
      completedAt,
      isCalendarEvent: true,
      isBirthday,
      htmlLink: evt.htmlLink || null,
      date: selectedDate
    };
  });

  const allTasksForSelectedDate = [...tasksForSelectedDate, ...calendarTasksForSelectedDate];

  const profileCounts = {};
  let uncategorizedCount = 0;
  allTasksForSelectedDate.forEach(t => {
    if (getTaskProfile(t)) {
      profileCounts[getTaskProfile(t)] = (profileCounts[getTaskProfile(t)] || 0) + 1;
    } else {
      uncategorizedCount += 1;
    }
  });

  const filteredTasks = allTasksForSelectedDate.filter(t => {
    if (!selectedProfile) return true;
    if (selectedProfile === '__none__') {
      return !getTaskProfile(t);
    }
    const taskCat = (getTaskProfile(t) || '').toLowerCase().trim();
    const selCat = (selectedProfile || '').toLowerCase().trim();
    return taskCat === selCat;
  });

  const processedTasks = filteredTasks.map(t => {
      if (t.isCalendarEvent) {
        return t;
      }

      const historyKey = `${t.id}_${selectedDate}`;
      const historyEntry = taskHistory[historyKey];

      let status = 'pending';
      let completed = false;
      let observation = '';
      let completedAt = null;
      let startedAt = null;

      if (historyEntry) {
        status = historyEntry.status || 'pending';
        completed = status === 'completed';
        observation = historyEntry.observation || '';
        completedAt = historyEntry.completedAt || null;
        startedAt = historyEntry.startedAt || null;
      } else {
        // Fallback para tarefas salvas antes da migração para taskHistory
        if (t.isRecurring) {
          const completedDates = Array.isArray(t.completedDates) ? t.completedDates : [];
          const failedDates = Array.isArray(t.failedDates) ? t.failedDates : [];
          const dateObservations = t.dateObservations || {};

          if (completedDates.includes(selectedDate)) {
            status = 'completed';
            completed = true;
          } else if (failedDates.includes(selectedDate)) {
            status = 'failed';
            completed = false;
          }
          observation = dateObservations[selectedDate] || '';
          startedAt = null;
          completedAt = null;
        } else if (t.date === selectedDate || (!t.date && selectedDate === todayStr)) {
          status = t.status || (t.completed ? 'completed' : 'pending');
          completed = status === 'completed';
          observation = t.observation || '';
          completedAt = t.completedAt || null;
          startedAt = t.startedAt || null;
        }
      }

      // Subtarefas isoladas para o dia:
      // Se houver histórico salvo no dia, usa-o; caso contrário, deriva do modelo base com completed: false
      const baseSubtasks = Array.isArray(t.subtasks) ? t.subtasks : [];
      let daySubtasks = [];
      if (historyEntry && Array.isArray(historyEntry.subtasks) && historyEntry.subtasks.length > 0) {
        daySubtasks = historyEntry.subtasks;
      } else {
        daySubtasks = baseSubtasks.map(st => ({
          ...st,
          completed: false
        }));
      }

      return {
        ...t,
        status,
        completed,
        observation,
        completedAt,
        startedAt,
        subtasks: daySubtasks
      };
    });

  const PERIOD_NAMES = ['Manhã', 'Tarde', 'Noite'];
  const groupedPeriodTasks = PERIOD_NAMES.map(pName => {
    return {
      period: pName,
      tasks: processedTasks.filter(t => getPeriod(t) === pName)
    };
  });

  const otherTasks = processedTasks.filter(t => !PERIOD_NAMES.includes(getPeriod(t)));
  if (otherTasks.length > 0) {
    groupedPeriodTasks.push({ period: 'Outros', tasks: otherTasks });
  }

  const getUserAvatar = (u) => {
    if (u) {
      const meta = u.user_metadata || {};
      if (meta.avatar_url) return meta.avatar_url;
      if (meta.picture) return meta.picture;

      if (u.identities && u.identities.length > 0) {
        for (const identity of u.identities) {
          const idData = identity.identity_data || {};
          if (idData.avatar_url) return idData.avatar_url;
          if (idData.picture) return idData.picture;
        }
      }
    }

    return null;
  };

  const userAvatarUrl = getUserAvatar(user);

  return (
    <div className="planner-container">
      <div className="top-navigation-bar">
        <div className="date-navigator-container">
          <button className="date-nav-btn" onClick={handlePrevDay} title="Dia anterior">
            <CaretLeft size={18} weight="bold" />
          </button>

          <div className="date-picker-badge" title="Clique para escolher uma data">
            <CalendarBlank size={18} weight="bold" className="calendar-icon" />
            <span className="date-display-text">{getFormattedDateLabel(selectedDate)}</span>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
              className="date-input-hidden"
            />
          </div>

          <button className="date-nav-btn" onClick={handleNextDay} title="Próximo dia">
            <CaretRight size={18} weight="bold" />
          </button>

          {selectedDate !== getTodayString() && (
            <button className="today-shortcut-btn" onClick={() => setSelectedDate(getTodayString())}>
              Hoje
            </button>
          )}
        </div>

        <div className="top-actions-right">
          <div className="theme-toggle-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.2rem' }}><AppIcon name={isDarkMode ? "moon" : "sun"} color="var(--primary-bg)" size={24} /></span>
            <label className="switch">
              <input type="checkbox" checked={isDarkMode} onChange={toggleDarkMode} />
              <span className="slider round"></span>
            </label>
          </div>

          {userAvatarUrl ? (
            <button
              className="user-auth-badge-btn user-avatar-only-btn"
              onClick={onOpenAuthModal}
              title={user?.email ? `Logado como ${user.email}` : "Perfil do Usuário"}
            >
              <div className="google-avatar-ring">
                <img src={userAvatarUrl} alt="Perfil" className="google-avatar-img" />
              </div>
            </button>
          ) : (
            <button className="user-auth-badge-btn" onClick={onOpenAuthModal} title={user ? `Logado como ${user.email}` : "Entrar ou Criar Conta"}>
              <User size={20} />
              <span>{user ? (user.email ? user.email.split('@')[0] : 'Minha Conta') : 'Entrar'}</span>
            </button>
          )}
        </div>
      </div>

      <div className="planner-header">
        <h1>Focus Task <AppIcon name="target" size={36} className="brand-icon" /></h1>
        <button className="add-task-button" onClick={() => setIsAddTaskModalOpen(true)}>
          <PlusCircle size={28} />
          <span>Nova Tarefa</span>
        </button>
      </div>

      <CategoryFilterBar
        profiles={plannerProfiles}
        profileCounts={profileCounts}
        totalCount={allTasksForSelectedDate.length}
        uncategorizedCount={uncategorizedCount}
        selectedProfile={selectedProfile}
        onSelectProfile={handleSelectCategory}
        weekday={getWeekdayLabel(selectedDate)}
      />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOnDragEnd}>
        <div
          className="planner-periods-wrapper"
          onDragOver={handleDragOverFromCalendar}
          onDrop={handleDropFromCalendar}
        >
          {processedTasks.length > 0 ? (
            groupedPeriodTasks.map((group) => {
              if (group.tasks.length === 0) return null;

              return (
                <div key={group.period} className="period-section-group">
                  <h2 className="period-section-title">{group.period}</h2>
                  <SortableContext items={group.tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                    <div className="period-section-box">
                      {group.tasks.map((task) => (
                        <TodoItem
                          key={task.id}
                          task={task}
                          onToggle={handleToggle}
                          onRemove={handleRemove}
                          onStartTimer={handleStartTimer}
                          onPauseResume={handlePauseResumeTimer}
                          onCancel={handleCancelTimer}
                          activeTimer={activeTimer}
                          currentTimeDisplay={currentTimeDisplay}
                          onOpenDetails={() => setSelectedTask(processedTasks.find(pt => pt.id === task.id) || task)}
                          selectedDate={selectedDate}
                          todayStr={todayStr}
                          onMoveToToday={handleMoveToToday}
                          onOpenObservation={(t) => setTaskForObservation(processedTasks.find(pt => pt.id === t.id) || t)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </div>
              );
            })
            ) : (
              <div className="period-section-group">
                <div className="period-section-box empty-box">
                  <p className="empty-state-message">Nenhuma tarefa encontrada</p>
                </div>
              </div>
            )}
          </div>
      </DndContext>
      {selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          selectedDate={selectedDate}
          todayStr={todayStr}
          onClose={() => setSelectedTask(null)}
          onUpdateTask={handleUpdateTask}
          onRemoveTask={handleRemove}
        />
      )}
      <AddTaskModal
        isOpen={isAddTaskModalOpen}
        onClose={() => setIsAddTaskModalOpen(false)}
        onAddTask={handleAddTask}
        taskTemplates={templates}
        selectedDate={selectedDate}
        initialProfile={selectedProfile && selectedProfile !== '__none__' ? selectedProfile : ''}
      />
      {taskForCompletion && (
        <TaskCompletionModal
          task={taskForCompletion}
          onClose={() => setTaskForCompletion(null)}
          onConfirm={(data) => handleSaveCompletion(taskForCompletion.id, data)}
          onResetPending={() => handleResetPending(taskForCompletion.id)}
        />
      )}
      {taskForObservation && (
        <TaskObservationModal
          task={taskForObservation}
          onClose={() => setTaskForObservation(null)}
          onEditObservation={(t) => {
            setTaskForObservation(null);
            setTaskForCompletion(t);
          }}
        />
      )}
      {taskToDelete && (
        <DeleteTaskModal
          task={taskToDelete}
          dateFormatted={(() => {
            if (!selectedDate) return '';
            const parts = selectedDate.split('-');
            return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : selectedDate;
          })()}
          onClose={() => setTaskToDelete(null)}
          onDeleteSingleDay={handleDeleteSingleDay}
          onDeleteFromDayForward={handleDeleteFromDayForward}
          onDeleteAll={handleDeleteAll}
        />
      )}
    </div>
  );
}

export default DailyPlanner;
